import { css } from "@emotion/react";
import PropTypes from "prop-types";
import useWindowResized from "@/helpers/useWindowResized";
import { useEffect, useRef, useState } from "react";
import TranscriptsApi from "@apis/TranscriptsApi";
import TranscriptIcon from "@/base/TranscriptIcon";
import axios from "axios";
import { buttonClick } from "@util/buttonClick";
import SVG from "react-inlinesvg";

const Video = ({ video_provider, video_id, autoplay, unmute, background, keepAspect, transcriptInline }) => {

    const [transcript, setTranscript] = useState(null);
    const [transcriptVisible, setTranscriptVisible] = useState(false);

    const iframeRef = useRef();
    const wrapperRef = useRef();

    useWindowResized(() => {

        if (iframeRef.current && background) {
            if ((wrapperRef.current.parentNode.offsetWidth / wrapperRef.current.parentElement.offsetHeight) > 1.77) {
                //set iframe width to parent width
                iframeRef.current.width = wrapperRef.current.parentNode.offsetWidth;
                iframeRef.current.height = wrapperRef.current.parentNode.offsetWidth * 56.2
            } else {
                //set iframe height to parent height
                iframeRef.current.height = wrapperRef.current.parentNode.offsetHeight;
                iframeRef.current.width = wrapperRef.current.parentNode.offsetHeight * 1.77
            }

        }

    })

    useEffect(() => {

        if (!video_provider || !video_id) return;

        const fetchTranscript = (type, id) => {

            new TranscriptsApi()
                .getTranscript(type, id)
                .then(res => {
                    console.log('response', res.data);

                    if (res.data.subtitles?.length > 0) {

                        let enTrack = res.data.subtitles.find(track => track['subtitle-language-short-name'] === 'en');
                        console.log('enTrack', enTrack);

                        axios.get(enTrack['subtitle-url']).then(res => {
                            if (res.data) {
                                setTranscript(res.data);
                            }
                        });
                    }

                })
                .catch(console.error);
        }

        fetchTranscript(video_provider, video_id.replace(/player\?GAOn=.*$/, ''));

        // Run Cookiebot scripts after the video is loaded
        if(window.Cookiebot) window.Cookiebot.runScripts();

    }, [video_provider, video_id]);

    const openCookiebot = () => {
        document.querySelector(".CookiebotWidget-logo").click();
    };

    const frameCss = css(`
        position: absolute;
        top:50%;
        left:50%;
        transform: translate(-50%, -50%);
        ${!background ? 'width:100%;' : ''}
        height:100%;
        background-color:black;
        ${background ? 'object-fit: cover;' : ''}
    `);

    const cookieConsentWarnCss = css(`
        position: relative;
        z-index: 10000;
        margin: 2rem;
        line-height: 4rem;
        color: #fff;
    `);

    const cookieConsentBtnCss = css(`
        background:none;
        border:none;
        margin:0;
        padding:0;
        cursor: pointer;
        color: #6594b0;
    `);

    const ekoEmbed = id => {
        const attributes = {
            height: "100%",
            "data-cookieblock-src": `https://video.eko.com/v/${id}/embed?autoplay=${autoplay ? "true" : "false"}`,
            "data-cookieconsent": "marketing, statistics, preferences",
            allow: "autoplay; fullscreen"
        };

        if (!background) attributes.width = '100%';

        return <div>
            <div
                className="cookieconsent-optout-marketing cookieconsent-optout-preferences cookieconsent-optout-statistics"
                css={cookieConsentWarnCss}>
                Please <button css={cookieConsentBtnCss} aria-label="open cookie consent banner" onClick={openCookiebot}>accept marketing, preferences, and statistics cookies</button> to enable the video player.
            </div>
            <iframe ref={iframeRef} css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
        </div>
    };

    const youtubeEmbed = id => {

        const urlArgs = {
            modestbranding: 1,
            rel: 0,
            "iv_load_policy": 3,
            showsearch: 0,
            autohide: 0,
        }

        // by coincidence this is pretty similar to the vimeo autoplay method, i suggest keeping this broken out to not kneecap any new video provider's ability to autoplay.
        if (autoplay) {
            urlArgs.autoplay = 1;
            urlArgs.controls = 0;
            urlArgs.showinfo = 0;
        }

        if (autoplay && !unmute) {
            urlArgs.muted = 1;
        }

        if (background) {
            urlArgs.muted = 1;
            urlArgs.playsinline = 1;
        }


        // Turn our k=>v object into a nice http GET formatted string.
        const finalArgs = Object.keys(urlArgs).map(i => `${i}=${urlArgs[i]}`).join("&");

        const attributes = {
            height: "100%",
            src: `https://www.youtube.com/embed/${id}?${finalArgs}`,
            frameBorder: 0,
            allow: "autoplay; fullscreen"
        };

        if (!background) attributes.width = '100%';

        return <iframe ref={iframeRef} css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
    };

    const vimeoEmbed = id => {

        const urlArgs = {
            color: "ffffff"
        }

        // by coincidence this is pretty similar to the youtube autoplay method, i suggest keeping this broken out to not kneecap any new video provider's ability to autoplay.
        if (autoplay) {
            urlArgs.autoplay = 1;
            urlArgs.controls = 0;
            urlArgs.showinfo = 0;
        }

        if (autoplay && !unmute) {
            urlArgs.muted = 1;
        }

        if (background) {
            urlArgs.background = 1
        }

        // Turn our k=>v object into a nice http GET formatted string.
        const finalArgs = Object.keys(urlArgs).map(i => `${i}=${urlArgs[i]}`).join("&");

        const attributes = {
            height: "100%",
            src: `https://player.vimeo.com/video/${id}?${finalArgs}`,
            frameBorder: 0,
            allow: "autoplay; fullscreen"
        };

        if (!background) attributes.width = '100%';

        return <iframe ref={iframeRef} css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
    };

    const vzaarEmbed = id => {

        const attributes = {
            height: "100%",
            "data-cookieblock-src": `https://view.vzaar.com/${id}/player?GAOn=${autoplay ? "true" : "false"}`,
            "data-cookieconsent": "statistics, preferences",
            frameBorder: 0,
            allow: "autoplay; fullscreen"
        };

        if (!background) attributes.width = '100%';

        return <div>
            <div
                className="cookieconsent-optout-preferences cookieconsent-optout-statistics"
                css={cookieConsentWarnCss}>
                Please <button css={cookieConsentBtnCss} aria-label="open cookie consent banner" onClick={openCookiebot}>accept preferences and statistics cookies</button> to enable the video player.
            </div>
            <iframe ref={iframeRef} css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
        </div>
    };

    const getRawEmbedCode = id => {

        const attributes = {
            width: "100%",
            height: "100%",
            muted: !unmute,
            autoPlay: autoplay,
            loop: autoplay,
            controls: !background,
            playsInline: true
        }

        /* eslint-disable jsx-a11y/media-has-caption */
        return (
            <video css={!keepAspect && frameCss} {...attributes} >
                <source src={id} type="video/mp4" />
            </video>
        )
    }

    const showEmbed = (video_provider, video_id) => {

        if (!video_provider || !video_id) return null;

        const embedMap = {
            eko: ekoEmbed,
            vimeo: vimeoEmbed,
            youtube: youtubeEmbed,
            vzaar: vzaarEmbed,
            raw_url: getRawEmbedCode
        };
        if (embedMap[video_provider]) return embedMap[video_provider](video_id);
        console.warn(`No Video Provider for ${video_provider}/${video_id}`)
        return null
    }

    const aspectRatioWrap = css(`
        padding-bottom: 56.25%; // 16:9
        width:100%;
        height:0;
        position: relative;
        background-color:black; // allow cookie warning to show, even if iframe not loaded
    `);

    const transcriptWrapper = css(`
        position: relative;
        margin: ${transcriptVisible ? '20px auto' : '0'};
        display: ${transcriptVisible ? 'block' : 'none'};
    `);

    const transcriptModalWrapper = css(`
        position: fixed;
        display: ${transcriptVisible ? 'block' : 'none'};
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: rgba(0,0,0,0.5);
        z-index: 1000;

        .modal-content{
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: white;
            padding: 2.5rem;
            border-radius: 5px;
            max-width: 80%;
            max-height: 80%;
            overflow: auto;
        }

        .close{
            position: absolute;
            top: 0px;
            right: 0px;
            cursor: pointer;
            transform: scale(.35);
        }
    `);

    const renderTranscriptModal = (inline) => {
        if (!transcript) return null;
        if (!inline) {
            return (
                <div css={transcriptModalWrapper}>
                    <div className={'modal-content'}>

                        <span className={'close'} tabIndex={0} onKeyDown={buttonClick} role={'button'} onClick={toggleTranscript}>
                            <SVG src={'/images/lac-icons/lac-nav-close.svg'} cacheRequests={true} />
                        </span>
                        <h4 title="Video Transcript">Video Transcript</h4>
                        {transcript}

                    </div>
                </div>
            );
        }
    };

    const transcriptInlineContainer = (inline) => {
        if (!transcript) return null;

        if (inline) {

            return (
                <div css={transcriptWrapper}>
                    <h4 title="Video Transcript">Video Transcript</h4>
                    {transcript}
                </div>
            )
        }
    };

    const transcriptIconCss = css(`
        position: absolute;
        top: 10px;
        left: 10px;
        widht: 48px;
        height: 48px;
        z-index: 10;
        border-radius: 50%;
        background: rgba(0,0,0,0.5);
        cursor: pointer;

        svg{
            position: relative;
            left: -3px;
        }
    `);

    const toggleTranscript = () => {
        setTranscriptVisible(!transcriptVisible);
    }


    return (
        <>
            <div ref={wrapperRef} css={(!background && !keepAspect) && aspectRatioWrap}>
                {transcript && <div css={transcriptIconCss} onClick={toggleTranscript} tabIndex={0} onKeyDown={buttonClick} role={'button'} aria-label='Show video transcript' title="Show video transcript"><TranscriptIcon /></div>}
                {renderTranscriptModal(transcriptInline)}
                {showEmbed(video_provider, video_id)}
                {/*{JSON.stringify([video_provider, video_id], false, '\t')}*/}
            </div>
            {transcriptInlineContainer(transcriptInline)}
        </>
    )
};

Video.propTypes = {
    video_provider: PropTypes.string,
    video_id: PropTypes.string,
    autoplay: PropTypes.bool,
    unmute: PropTypes.bool,
    background: PropTypes.bool,
    keepAspect: PropTypes.bool,
    transcriptInline: PropTypes.bool
};


export default Video;
